import { useEffect, useRef, useState } from 'react';
import { callApi } from 'src/utils/apiCall';

// Recordings stored by the backend (provider-neutral calls: Elevate etc.) are
// served from an authenticated API route (`/api/v1/practices/{p}/voice-calls/
// {id}/recording/`). An <audio> element cannot send the bearer token, so we
// first ask the sibling `playback-url/` route — same permission checks, JSON
// instead of audio — where to play the recording from. It answers either with
// a pre-signed, expiring storage URL (`direct`), which the element streams
// with range requests so the duration appears at once and seeking does not
// re-download, or with the download route (`proxy`) for audio that cannot be
// signed, which still has to land whole as a blob before playback. Absolute
// URLs (Twilio / WhatsApp media) are handed to the element unchanged.
export const isApiRecordingUrl = (src) => typeof src === 'string' && src.startsWith('/api/');
export const playbackUrlEndpoint = (src) => {
  const [path, query] = src.split('?');
  return `${path}playback-url/${query ? `?${query}` : ''}`;
};
export async function resolveRecordingSource(src, { signal } = {}) {
  if (!isApiRecordingUrl(src)) return { url: src, revoke: null };
  // Shared transport owns token refresh; a failed download stays local to the
  // player so the user can retry without disrupting the call review panel.
  const located = await callApi(playbackUrlEndpoint(src), 'GET', null, false, {
    signal, maxRetries: 0, suppressServerErrorDispatch: true,
  });
  if (located.ok && located.data?.mode === 'direct' && located.data.url) {
    // Streamed straight from storage — there is no blob to revoke.
    return { url: located.data.url, revoke: null };
  }
  // 404 also covers a backend deployed before this route existed: fall through
  // to the download that has always worked rather than breaking playback.
  if (!located.ok && located.status !== 404) throw new Error(`recording fetch failed (${located.status})`);
  const response = await callApi(src, 'GET', null, false, {
    responseType: 'blob', signal, maxRetries: 0, timeout: 60000, suppressServerErrorDispatch: true,
  });
  if (!response.ok) throw new Error(`recording fetch failed (${response.status})`);
  const url = URL.createObjectURL(response.data);
  return { url, revoke: () => URL.revokeObjectURL(url) };
}

export const clampSeek = (seconds, duration) =>
  Math.min(Math.max(0, Number.isFinite(seconds) ? seconds : 0), Math.max(0, Number.isFinite(duration) ? duration : 0));
const initial = { playing: false, duration: 0, time: 0, rate: 1, volume: 1, error: null, ready: false };

export default function useCallAudioPlayer({ src }) {
  const audioRef = useRef(null);
  const [media, setMedia] = useState(initial);
  const generation = useRef(0);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const audio = audioRef.current;
    generation.current += 1;
    const token = generation.current;
    setMedia(initial);
    if (!audio) return undefined;
    let revoke = null;
    const controller = new AbortController();
    audio.playbackRate = 1;
    audio.volume = 1;
    if (src) {
      resolveRecordingSource(src, { signal: controller.signal }).then((resolved) => {
        if (token !== generation.current) { resolved.revoke?.(); return; }
        revoke = resolved.revoke;
        // Assigning src starts the load; an explicit load() here would reset
        // the element and fire an error in some engines.
        audio.src = resolved.url;
      }).catch(() => {
        if (token === generation.current) setMedia((old) => ({ ...old, playing: false, error: 'Audio could not be loaded. Retry playback or refresh the call list for an updated recording link.' }));
      });
    }
    return () => {
      generation.current += 1;
      controller.abort();
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      revoke?.();
    };
  }, [src, attempt]);
  const update = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setMedia((old) => ({
      ...old,
      duration: Number.isFinite(audio.duration) ? audio.duration : 0,
      time: audio.currentTime || 0,
      playing: !audio.paused && !audio.ended,
      rate: audio.playbackRate,
      volume: audio.volume,
      ready: audio.readyState >= 1,
    }));
  };
  const fail = () =>
    setMedia((old) => ({
      ...old,
      playing: false,
      error: 'Audio could not be loaded. Retry playback or refresh the call list for an updated recording link.',
    }));
  const toggle = async () => {
    const audio = audioRef.current;
    if (!audio || !src) return;
    const token = generation.current;
    if (!audio.paused) {
      audio.pause();
      return;
    }
    try {
      await audio.play();
    } catch (_) {
      if (token === generation.current)
        setMedia((old) => ({
          ...old,
          playing: false,
          error: 'Playback could not start. Try again or use the browser audio controls.',
        }));
    }
  };
  const seek = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = clampSeek(seconds, media.duration);
      update();
    }
  };
  const setRate = (rate) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
      update();
    }
  };
  const setVolume = (volume) => {
    if (audioRef.current) {
      audioRef.current.volume = Math.min(1, Math.max(0, volume));
      update();
    }
  };
  const retry = () => {
    setMedia(initial);
    setAttempt((value) => value + 1);
  };
  return { audioRef, media, update, fail, toggle, seek, setRate, setVolume, retry };
}
